/**
 * The library's filter state and its translation to querystring params.
 *
 * Split out of `AssetFilters.tsx` so that file exports only its component
 * (React Fast Refresh gives up on a module that mixes components with plain
 * values), and so the translation can be unit-tested without rendering.
 */

import type { AssetListParams } from '@/services/assets';

export interface AssetFilterState {
  kind: '' | 'raster' | 'vector';
  extension: string;
  tag: string;
  usage: '' | 'used' | 'unused';
  minWidth: string;
  createdAfter: string;
  ordering: string;
}

export const EMPTY_FILTERS: AssetFilterState = {
  kind: '',
  extension: '',
  tag: '',
  usage: '',
  minWidth: '',
  createdAfter: '',
  ordering: '-created_at',
};

export const ORDERING_OPTIONS = [
  { value: '-created_at', label: 'Newest first' },
  { value: 'created_at', label: 'Oldest first' },
  { value: 'original_filename', label: 'File name A→Z' },
  { value: '-bytes', label: 'Largest file' },
  { value: 'bytes', label: 'Smallest file' },
  { value: '-width', label: 'Widest' },
];

/** Turn the UI state into the querystring the API expects. */
export function filtersToParams(filters: AssetFilterState): AssetListParams {
  const minWidth = Number(filters.minWidth);
  return {
    kind: filters.kind || undefined,
    extension: filters.extension || undefined,
    tag: filters.tag.trim() || undefined,
    // `in_use` is tri-state on the server: absent means "both". Sending an
    // empty string, or `false` for "both", would hide every image in use.
    in_use: filters.usage === '' ? undefined : filters.usage === 'used',
    min_width: Number.isFinite(minWidth) && minWidth > 0 ? minWidth : undefined,
    created_after: filters.createdAfter || undefined,
    ordering: filters.ordering || '-created_at',
  };
}

/** How many narrowing filters are on — the badge on the Filters button. */
export function activeFilterCount(filters: AssetFilterState): number {
  return [
    filters.kind,
    filters.extension,
    filters.tag.trim(),
    filters.usage,
    filters.minWidth,
    filters.createdAfter,
  ].filter(Boolean).length;
}
